// The STATED HOURS of the PLAY section of the hub SETTINGS panel
// (player-reach.md §5.3, R1304; the look of R1731).
//
// What it shows: the stated hours of the squad finder as a radio list of
// five choices — none, morning, afternoon, evening and night — and the
// time zone of the browser, which the card sends with the band. The meta server turns the
// band into a UTC block at the next season roll, and the world sorts the
// players of a banner who hold no clan by it.
//
// The stated hours are an account setting, but NOT a key of the settings
// blob: the meta server acts on them, so they travel on their own route
// (`PUT /api/settings/stated-hours`), like the standing-orders switch
// beside it. A press writes at once and is never debounced. The hours stay
// in the hub: the ACCOUNT page of the game menu links them (R1731).
//
// FIND A CLAN of the CLAN panel reads the same hours (R1908,
// player-reach.md §5.5): a write drops its hours match, so the list
// follows the new band.
//
// The card shows only for an account in no clan (R1319): the finder sorts
// the clanless players alone, so the hours of a clan member do nothing.
// The card reads the `clan` cell of the CLAN panel, and it draws nothing
// while that read travels, so it never shows and then goes. Only the
// `no_clan` answer shows the card: a clan read that fails for another
// reason does not say that the account is in no clan, so the card stays
// hidden (R1563).

import { oneOf } from '../../../util/oneof';
import {
  HOURS_BANDS, getSettings, putStatedHours, type HoursBand, type StoredSettings,
} from '../../../net/api';
import { hubHours as S } from '../../../gen/strings';
import { TTL_ON_OPEN, zoneLabel, type Cell } from '../hubstate';
import { valueChoice } from '../../choice/choice';
import { groupHead, note, row } from '../../options/kit';
import type { PanelContext } from '../panel';
import { CLAN_HOURS_CELL, clanCell, isNoClan } from './clan';
import { errNote, runHeld, type HeldCall } from './optcards';

/** The cache key of the typed settings (`GET /api/settings`). The standing
 *  orders read the same cell, in the hub and in the game menu, and a write
 *  of either drops it. */
export const SETTINGS_TYPED_CELL = 'settings-typed';

/** The typed settings, through the hub cache. */
export function storedSettingsCell(ctx: Pick<PanelContext, 'data'>): Cell<StoredSettings> {
  return ctx.data.cell<StoredSettings>(SETTINGS_TYPED_CELL, TTL_ON_OPEN, getSettings);
}

/** The name and the quiet line of each choice, in the order of HOURS_BANDS. */
const BAND_WORDS: Record<HoursBand, [name: string, desc: string]> = {
  '': [S.band_none, S.band_none_desc],
  morning: [S.band_morning, S.band_morning_desc],
  afternoon: [S.band_afternoon, S.band_afternoon_desc],
  evening: [S.band_evening, S.band_evening_desc],
  night: [S.band_night, S.band_night_desc],
};

/** The part of a panel the SETTINGS panel mounts. */
export interface HoursCard {
  html(): string;
  /** Takes one press. Answers false for an action that is not the card's. */
  act(action: string, el: HTMLElement): boolean;
}

/** The IANA time zone of this browser, or UTC when it names none. */
function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** `heading` writes the group head as an `h3`, the level of the hub
 *  outline (R2048). */
export function createHoursCard(
  ctx: PanelContext, zone: () => string = browserTimeZone, heading = false,
): HoursCard {
  /** The band as the last press left it, or undefined while the card
   *  shows what the server answered. */
  let picked: HoursBand | undefined;
  const st: HeldCall = { busy: false, err: '' };

  /** The band in force as the server sent it, or undefined while the
   *  first read travels. A server that predates the field reads as no
   *  statement. The word stays raw, so a band this build does not know
   *  marks no choice, and NONE is a change the card can write (R1343). */
  function bandNow(): string | undefined {
    if (picked !== undefined) return picked;
    const read = storedSettingsCell(ctx);
    if (read.state !== 'ready') return undefined;
    return read.value?.statedHours?.hours ?? '';
  }

  /** The band whose line the card shows: an unknown band shows the line of
   *  NONE. */
  function knownBand(now: string): HoursBand {
    return oneOf(HOURS_BANDS, now) ?? '';
  }

  /** One press on a choice. It writes at once, and the card shows the
   *  band the server answered. */
  function pick(band: HoursBand): void {
    const now = bandNow();
    if (st.busy || now === undefined || band === now) return;
    runHeld(ctx, st, 'sfx-menu-toggle', () => putStatedHours(band, zone()), (r) => {
      picked = r.statedHours.hours;
      ctx.data.drop(SETTINGS_TYPED_CELL);
      // FIND A CLAN lists the clans of the stated hours (R1908).
      ctx.data.drop(CLAN_HOURS_CELL);
    });
  }

  /** True when the clan read answered the 404 `no_clan` of an account in
   *  no clan. A ready read holds the account's clan, and another refusal
   *  says nothing about it (R1563). */
  function clanless(): boolean {
    const read = clanCell(ctx);
    return read.state === 'failed' && isNoClan(read.error);
  }

  return {
    html(): string {
      // Both reads start in this render, so neither waits for the other.
      const now = bandNow();
      if (!clanless()) return '';
      // One saved value: the chooser gives five bands an option list, one a
      // line at 390 px, and the one in force wears the mark (R2011). A
      // press while a write travels is dropped in `pick`.
      const choices = HOURS_BANDS.map((b) => ({ id: b, label: BAND_WORDS[b][0] }));
      const desc = now === undefined ? '' : ` · ${BAND_WORDS[knownBand(now)][1]}`;
      return groupHead(S.head, now === undefined ? S.checking : '', '', heading)
        + note(S.lede)
        + row({ name: S.hours, what: S.hours_desc({ zone: zoneLabel(zone(), ctx.now()) }) + desc, control: '' })
        + valueChoice({ list: choices, chosen: now ?? '', act: 'hours', label: S.head, uid: 'hub-hours' })
        + errNote(st.err);
    },

    act(action: string, el: HTMLElement): boolean {
      if (action !== 'hours') return false;
      const band = oneOf(HOURS_BANDS, el.dataset.id ?? '');
      if (band !== undefined) pick(band);
      return true;
    },
  };
}
